"use client";

import { useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Shield,
  Sparkles,
  Unlink,
} from "lucide-react";
import { Badge } from "@/components/common/ui/Badge";
import type { BadgeVariant } from "@/components/common/ui/Badge";
import { Tooltip } from "@/components/common/ui/Tooltip";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import { ReportButton } from "@/components/reports/ReportButton";
import { CarrierReportModal } from "./CarrierReportModal";
import {
  CarrierLogo,
  CarrierName,
  InlineReRateButton,
  PortalLinkButton,
  ReRateButton,
} from "./CarrierTileParts";
import { QuoteIdCompact } from "./QuoteId";
import type { CarrierCardProps } from "./types";

// --- Formatting helpers ---

function formatCurrency(amount: number, fractionDigits = 0): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1).replace(/\.0$/, "")}%`;
}

function formatScore(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function scoreVariant(score: number): BadgeVariant {
  if (score >= 7) return "success";
  if (score >= 4) return "warning";
  return "error";
}

// --- Visual variant tables ---

const BORDER_CLASSES: Record<string, string> = {
  default: "border-gray-200",
  success: "border-emerald-200",
  warning: "border-amber-300",
  error: "border-red-300",
};

const BREAKDOWN_COLORS: Record<string, { bg: string; text: string }> = {
  blue: { bg: "bg-blue-50", text: "text-blue-700" },
  purple: { bg: "bg-purple-50", text: "text-purple-700" },
  green: { bg: "bg-green-50", text: "text-green-700" },
  amber: { bg: "bg-amber-50", text: "text-amber-700" },
};

export function CarrierCard({
  carrier,
  isSelected,
  onToggle,
  expanded: controlledExpanded,
  onToggleExpand,
  premium,
  premiumBreakdowns,
  score,
  scoreDescription,
  commission,
  quoteId,
  quoteIdParts,
  effectiveDate,
  alert: alertProp,
  submissionAdjustmentIndicator,
  resultBadges,
  link,
  portalLinkUnsupported,
  error,
  reRateAction,
  report,
  expandedContent,
  borderVariant: borderVariantOverride,
}: CarrierCardProps) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const expanded =
    controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const toggleExpand =
    onToggleExpand || (() => setInternalExpanded((prev) => !prev));

  const alert = alertProp;
  // Auto-determine border variant
  const borderVariant =
    borderVariantOverride ??
    (error
      ? "error"
      : alert && (alert.variant === "referral" || alert.variant === "warning")
        ? "warning"
        : premium !== undefined
          ? "success"
          : "default");

  const borderClass = BORDER_CLASSES[borderVariant] || BORDER_CLASSES.default;

  // Expandability
  const hasExpandedContent =
    expandedContent !== null && expandedContent !== undefined;
  const hasExpandedFooterActions =
    !!report || !!link || !!(reRateAction?.showInFooter);
  const hasExpandableContent =
    !!error || hasExpandedContent || hasExpandedFooterActions;

  const showAlertBadge = !!alert && alert.badgeLabel.length > 0;

  const showInlinePreview = !!alert?.inlinePreview && !expanded;

  const hasQuoteId = !!quoteId || (quoteIdParts && quoteIdParts.length > 0);

  return (
    <div
      className={cn("@container bg-white rounded-lg border transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)]", borderClass)}
    >
      {/* Main row */}
      <div
        className="flex items-center gap-4 px-4 py-3 cursor-pointer select-none"
        onClick={() => toggleExpand()}
      >
        {/* Selection checkbox */}
        <div
          className="flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <div
            className={cn(
              "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors cursor-pointer",
              isSelected
                ? "bg-blue-500 border-blue-500"
                : "border-gray-300 hover:border-blue-400",
            )}
          >
            {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
          </div>
        </div>

        {/* Expand chevron */}
        <div
          className={cn("flex-shrink-0 text-gray-400", !hasExpandableContent && "invisible")}
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </div>

        {/* Carrier logo — hidden when container is very narrow */}
        <CarrierLogo carrier={carrier} className="hidden @3xl:flex" />

        {/* Carrier name */}
        <CarrierName carrier={carrier} />

        {/* Premium cell */}
        <div className="w-36 flex-shrink-0">
          {error ? (
            <Typography as="span" variant="headline5" weight="medium" color="error">
              {error.title}
            </Typography>
          ) : premium !== undefined ? (
            <>
              <Typography as="div" variant="headline3" weight="bold">
                {formatCurrency(premium)}
              </Typography>
              {premiumBreakdowns && premiumBreakdowns.length > 0 && (
                <div className="flex gap-2 mt-0.5">
                  {premiumBreakdowns.map((bd) => {
                    const colors =
                      BREAKDOWN_COLORS[bd.color || "blue"] ||
                      BREAKDOWN_COLORS.blue;
                    return (
                      <Typography
                        as="span"
                        key={bd.label}
                        variant="small"
                        weight="medium"
                        color={colors.text}
                        className={cn("px-1.5 py-0.5 rounded", colors.bg)}
                      >
                        {bd.label}: {formatCurrency(bd.amount)}
                      </Typography>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <Typography as="span" variant="headline5" color="text-gray-400">
              &mdash;
            </Typography>
          )}
        </div>

        {/* Commission cell — hidden at narrow container */}
        <div className="w-28 flex-shrink-0 text-center hidden @4xl:block">
          {commission && premium !== undefined ? (
            <div>
              <Tooltip
                content={`${commission.label || "Default"} commission rate`}
                placement="top"
              >
                <Badge variant="success" size="sm">
                  {formatRate(commission.rate)}
                </Badge>
              </Tooltip>
              <Typography
                as="div"
                variant="body"
                weight="medium"
                color="success"
                className="mt-0.5"
              >
                {formatCurrency(commission.amount, 2)}
              </Typography>
            </div>
          ) : (
            <Typography as="span" variant="body" color="text-gray-400">
              &mdash;
            </Typography>
          )}
        </div>

        {/* Quote ID — hidden at narrow container */}
        <div className="w-32 flex-shrink-0 hidden @5xl:block">
          {hasQuoteId ? (
            <QuoteIdCompact quoteId={quoteId} quoteIdParts={quoteIdParts} />
          ) : (
            <Typography as="span" variant="body" color="text-gray-400">
              &mdash;
            </Typography>
          )}
        </div>

        {/* Effective Date — hidden at narrow container */}
        <div className="w-24 flex-shrink-0 hidden @6xl:block">
          <Typography as="span" variant="body" color="muted">
            {effectiveDate || "—"}
          </Typography>
        </div>

        {/* Alerts badges — hidden at narrow container */}
        <div className="flex-shrink-0 w-24 @5xl:w-32 hidden @3xl:flex justify-start">
          <div className="flex flex-col items-start gap-1">
            {score !== null && score !== undefined && (
              <Tooltip
                content={
                  scoreDescription
                    ? `Policy score: ${formatScore(score)} - ${scoreDescription}`
                    : `Policy score: ${formatScore(score)}`
                }
                placement="top"
              >
                <Badge
                  variant={scoreVariant(score)}
                  size="xs"
                  icon={Shield}
                  className="cursor-default"
                >
                  Score {formatScore(score)}
                </Badge>
              </Tooltip>
            )}

            {showAlertBadge && alert && (
              <Tooltip
                content={
                  alert.description ? (
                    <Typography as="span" variant="body">
                      {alert.description}
                    </Typography>
                  ) : alert.items && alert.items.length === 1 ? (
                    <Typography as="span" variant="body">
                      {alert.items[0]}
                    </Typography>
                  ) : alert.items && alert.items.length > 1 ? (
                    <Typography
                      as="ul"
                      variant="body"
                      className="list-disc pl-4 py-1 space-y-1.5"
                    >
                      {alert.items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </Typography>
                  ) : (
                    <Typography as="span" variant="body">
                      {alert.title}
                    </Typography>
                  )
                }
                placement="top"
              >
                <Badge
                  variant={alert.variant === "info" ? "info" : "warning"}
                  size="xs"
                  icon={alert.aiSummarized ? Sparkles : alert.icon}
                  className="cursor-default"
                >
                  {alert.badgeLabel}
                </Badge>
              </Tooltip>
            )}

            {submissionAdjustmentIndicator && (
              <Tooltip
                content={
                  <Typography as="span" variant="body">
                    {submissionAdjustmentIndicator.tooltip}
                  </Typography>
                }
                placement="top"
              >
                <Badge
                  variant="info"
                  size="xs"
                  icon={submissionAdjustmentIndicator.icon}
                  className="cursor-default"
                >
                  {submissionAdjustmentIndicator.label}
                </Badge>
              </Tooltip>
            )}

            {resultBadges?.map((resultBadge) => (
              <Tooltip
                key={`${resultBadge.label}-${resultBadge.tooltip}`}
                content={
                  <Typography as="span" variant="body">
                    {resultBadge.tooltip}
                  </Typography>
                }
                placement="top"
              >
                <Badge
                  variant={resultBadge.variant ?? "info"}
                  size="xs"
                  icon={resultBadge.icon}
                  className="cursor-default"
                >
                  {resultBadge.label}
                </Badge>
              </Tooltip>
            ))}
          </div>
        </div>

        {/* Spacer + inline preview (absorbs remaining space between columns and portal link) */}
        <div className="flex-1 min-w-0 flex items-center pl-4">
          {showInlinePreview && alert && (
            <Typography
              as="span"
              variant="body"
              color={
                alert.variant === "info" ? "text-blue-600" : "text-amber-600"
              }
              truncate
              title={alert.inlinePreview}
            >
              {alert.inlinePreview}
            </Typography>
          )}
        </div>

        {/* Portal link / No portal link — hidden at narrow container */}
        <div className="w-24 flex-shrink-0 items-center justify-center hidden @2xl:flex">
          {link ? (
            <PortalLinkButton link={link} />
          ) : premium !== undefined ? (
            portalLinkUnsupported ? (
              <Tooltip
                content={`${carrier.name} does not provide portal links. Use the quote ID to find this quote in their portal.`}
              >
                <Unlink
                  className="w-4 h-4 text-gray-400"
                  aria-label={`${carrier.name} does not provide portal links`}
                />
              </Tooltip>
            ) : (
              <Typography
                as="span"
                variant="small"
                weight="medium"
                color="text-gray-400"
              >
                No portal link
              </Typography>
            )
          ) : null}
        </div>

        {/* Re-rate — hidden at narrow container */}
        {reRateAction && (
          <div className="hidden @3xl:contents">
            <ReRateButton
              onReRate={reRateAction.onClick}
              disabled={reRateAction.disabled}
              title={reRateAction.title}
            />
          </div>
        )}
      </div>

      {/* Expanded detail */}
      {expanded && hasExpandableContent && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
          {/* Error detail */}
          {error && (
            <div
              className={cn(
                "bg-red-50 border border-red-200 rounded-lg p-3",
                (hasExpandedContent || hasExpandedFooterActions) && "mb-3",
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <Typography
                  as="span"
                  variant="headline5"
                  weight="semibold"
                  color="text-red-900"
                >
                  {error.title}
                </Typography>
              </div>
              <Typography
                as="p"
                variant="body"
                color="text-red-700"
                leading="leading-relaxed"
                className="whitespace-pre-line break-words"
              >
                {error.message}
              </Typography>
            </div>
          )}

          {hasExpandedContent && (
            <div className={cn(hasExpandedFooterActions && "mb-3")}>
              {expandedContent}
            </div>
          )}

          {/* Footer: Report + Portal Link + Re-rate */}
          {hasExpandedFooterActions && (
            <div className="pt-3 border-t border-gray-200 flex items-center gap-3">
              {report && (
                <>
                  <ReportButton
                    size="sm"
                    onClick={() => setReportModalOpen(true)}
                  />
                  <CarrierReportModal
                    isOpen={reportModalOpen}
                    onClose={() => setReportModalOpen(false)}
                    applicationId={report.applicationId}
                    carrierName={report.carrierName}
                    carrierDisplayName={report.carrierDisplayName}
                    coverageType={report.coverageType}
                    quoteData={report.quoteData}
                    vertical={report.vertical}
                    taskId={report.taskId}
                    initialComments={report.initialComments}
                  />
                </>
              )}
              <div className="ml-auto flex items-center gap-2">
                {link && (
                  <span className="@2xl:hidden">
                    <PortalLinkButton link={link} />
                  </span>
                )}
                {reRateAction?.showInFooter && (
                  <InlineReRateButton
                    onReRate={reRateAction.onClick}
                    disabled={reRateAction.disabled}
                    title={reRateAction.title}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
